import '@/styles/globals.css'

import type { FC, ReactNode } from 'react'
import type { AppProps } from 'next/app'

import Head from 'next/head'
import { useEffect, useMemo, useState } from 'react'
import { Cormorant_Garamond, IBM_Plex_Mono, Manrope } from 'next/font/google'
import { primeUniverse } from '@/lib/universe/store'
import BlackHoleCanvas from '@/components/universe/black-hole-canvas'
import Cursor from '@/components/universe/cursor'
import Grain from '@/components/universe/grain'
import { Intro } from '@/components/universe/intro'
import { PageTransition } from '@/components/universe/page-transition'
import { MenuContext } from '@/components/generals/menu-context'
import { SiteHeader } from '@/components/generals/site-header'
import { MenuOverlay } from '@/components/generals/menu-overlay'
import { DistanceReadout } from '@/components/generals/distance-readout'

const sans = Manrope({
    subsets: ['latin']
})

// Light, cinematic garalde for every headline, used at weight 300
const heading = Cormorant_Garamond({
    subsets: ['latin'],
    weight: ['300', '400'],
    style: ['normal', 'italic']
})

const mono = IBM_Plex_Mono({
    subsets: ['latin'],
    weight: ['400', '500']
})

const fontVariables = `:root{--font-app-sans:${sans.style.fontFamily};--font-app-mono:${mono.style.fontFamily};--font-app-heading:${heading.style.fontFamily};--font-app-serif:${heading.style.fontFamily}}`

const App: FC<AppProps> = ({ Component, pageProps, router }): ReactNode => {
    const [menuOpen, setMenuOpen] = useState(false)
    const menu = useMemo(() => ({ open: menuOpen, setOpen: setMenuOpen }), [menuOpen])
    const initialPath = router.asPath

    useEffect(() => {
        primeUniverse(initialPath)
    }, [initialPath])

    return (
        <div className='h-full overflow-x-clip font-sans'>
            <Head>
                <style dangerouslySetInnerHTML={{ __html: fontVariables }} />
                <meta name='viewport' content='width=device-width, initial-scale=1, viewport-fit=cover' />
                <meta name='theme-color' content='#000000' />
            </Head>

            <MenuContext.Provider value={menu}>
                <BlackHoleCanvas />
                <Intro />
                <PageTransition Component={Component} pageProps={pageProps} />
                <SiteHeader />
                <DistanceReadout />
                <MenuOverlay />
                <Cursor />
                <Grain />
            </MenuContext.Provider>
        </div>
    )
}

export default App
